import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Inbox, Flame, ShieldCheck, CheckCircle2, XCircle, Building2,
  ExternalLink, Mail, Clock, History, Wallet, Handshake, AlertCircle,
  Brain, TrendingUp, Sparkles, RefreshCw, Linkedin, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const TABS = [
  { key: "replies", label: "Hot replies", icon: Flame, color: "text-destructive" },
  { key: "approval", label: "Approval queue", icon: ShieldCheck, color: "text-amber-accent" },
];

const INTENT_STYLE = {
  partnership_inquiry: { label: "🤝 Partnership", bg: "bg-emerald-500/10 text-emerald-500 border-emerald-500/40", priority: 3 },
  quote_request:       { label: "💰 Quote", bg: "bg-signal-500/10 text-signal-500 border-signal-500/40", priority: 2 },
  scheduling:          { label: "📅 Schedule", bg: "bg-signal-500/10 text-signal-500 border-signal-500/40", priority: 2 },
  objection:           { label: "❓ Objection", bg: "bg-amber-accent/10 text-amber-accent border-amber-accent/40", priority: 1 },
  not_interested:      { label: "✋ Pass", bg: "bg-muted/30 text-muted-foreground border-border", priority: 0 },
  general:             { label: "💬 General", bg: "bg-muted/30 text-muted-foreground border-border", priority: 1 },
  unclear:             { label: "❔ Unclear", bg: "bg-muted/30 text-muted-foreground border-border", priority: 1 },
};

export default function InboxPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "approval" ? "approval" : "replies";
  const [replies, setReplies] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get("/inbox/replies?limit=50"),
        api.get("/dashboard"),
      ]);
      // Sort replies: partnership > quote > schedule > objection > general (then by date desc)
      const sorted = (r.data.replies || []).slice().sort((a, b) => {
        const pa = (INTENT_STYLE[a.last_reply_intent] || INTENT_STYLE.general).priority;
        const pb = (INTENT_STYLE[b.last_reply_intent] || INTENT_STYLE.general).priority;
        if (pa !== pb) return pb - pa;
        return (b.replied_at || "").localeCompare(a.replied_at || "");
      });
      setReplies(sorted);
      setApprovalQueue(d.data?.approval_queue || []);
    } catch { toast.error("Failed to load inbox"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handoff = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/inbox/${id}/handoff-to-erp`);
      const b = data?.brief || {};
      if (b.email_sent) {
        toast.success("Handed off — brief emailed to you", {
          description: `Full context sent to ${b.email_to}. Quote them in myEuropacific.`,
        });
      } else {
        toast.success("Handed off to ERP — sequence stopped", {
          description: "Couldn't email the brief (check M365 connection). Open the contact to take over manually.",
        });
      }
      await load();
    } catch (e) {
      toast.error(`Handoff failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setBusyId(null);
  };

  const approve = async (id, edits = {}) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/sequencer/approve/${id}`, edits);
      if (data.send_result?.ok) {
        toast.success("Approved + invite fired");
      } else {
        toast.warning(`Approved, but send failed: ${data.send_result?.error}`);
      }
      await load();
    } catch (e) {
      toast.error(`Approval failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setBusyId(null);
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/sequencer/reject/${id}`);
      toast.success("Skipped");
      await load();
    } catch (e) {
      toast.error(`Skip failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setBusyId(null);
  };

  return (
    <DashboardLayout
      eyebrow="Inbox"
      title="Unified replies + approvals"
    >
      <div className="flex gap-1 mb-6 border-b border-border" data-testid="inbox-tabs">
        {TABS.map(({ key, label, icon: Icon, color }) => {
          const count = key === "replies" ? replies.length : approvalQueue.length;
          return (
            <button
              key={key}
              onClick={() => setParams({ tab: key })}
              data-testid={`inbox-tab-${key}`}
              className={`px-4 py-2 text-xs font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === key ? "border-signal-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className={`w-3.5 h-3.5 ${tab === key ? color : ""}`} />
              {label}
              {count > 0 && (
                <span className={`text-[0.6rem] font-mono px-1.5 py-0.5 rounded-sm border ${tab === key ? "border-signal-500/40 bg-signal-500/10 text-signal-500" : "border-border bg-muted/30 text-muted-foreground"}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "replies" && (
        <div className="space-y-3" data-testid="inbox-replies-list">
          {loading && replies.length === 0 ? (
            <p className="text-xs text-muted-foreground">Loading replies…</p>
          ) : replies.length === 0 ? (
            <div className="data-card text-center py-12">
              <Inbox className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">No replies yet</h3>
              <p className="text-xs text-muted-foreground mt-1">Once cold leads reply to your LinkedIn or email outreach, they&apos;ll appear here for triage.</p>
            </div>
          ) : (
            replies.map((p) => (
              <ReplyCard key={p.id} prospect={p} onHandoff={() => handoff(p.id)} busy={busyId === p.id} />
            ))
          )}
        </div>
      )}

      {tab === "approval" && (
        <div className="space-y-3" data-testid="inbox-approval-list">
          {loading && approvalQueue.length === 0 ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : approvalQueue.length === 0 ? (
            <div className="data-card text-center py-12">
              <ShieldCheck className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">Nothing to approve</h3>
              <p className="text-xs text-muted-foreground mt-1">High-confidence sends happen automatically. Risky drafts (low-fit / money keywords) land here for your decision.</p>
            </div>
          ) : (
            approvalQueue.map((p) => (
              <ApprovalCard key={p.id} prospect={p} onApprove={(e) => approve(p.id, e)} onReject={() => reject(p.id)} busy={busyId === p.id} />
            ))
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

export function ReplyCard({ prospect, onHandoff, onSkip, busy }) {
  const erp = prospect.erp_relationship || {};
  const intent = INTENT_STYLE[prospect.last_reply_intent] || INTENT_STYLE.general;
  const isPartnership = prospect.last_reply_intent === "partnership_inquiry";
  const isQuote = prospect.last_reply_intent === "quote_request";
  const [brain, setBrain] = useState(null);
  const [brainLoading, setBrainLoading] = useState(false);

  const loadBrain = async () => {
    if (brain || brainLoading) return;
    setBrainLoading(true);
    try {
      const { data } = await api.get(`/inbox/${prospect.id}/quote-brain`);
      setBrain(data);
    } catch { /* silent — Quote Brain is an enhancement, not critical */ }
    setBrainLoading(false);
  };

  useEffect(() => {
    if (isQuote) loadBrain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuote]);
  return (
    <div className={`data-card ${isPartnership ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/30 bg-destructive/5"}`} data-testid={`reply-${prospect.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm inline-flex items-center gap-2">
            {prospect.full_name}
            <span className={`px-1.5 py-0.5 rounded-sm text-[0.55rem] tracking-widest uppercase font-mono border ${intent.bg}`}>
              {intent.label}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">{prospect.title}</div>
          <div className="text-xs text-signal-500 inline-flex items-center gap-1.5 mt-0.5">
            <Building2 className="w-3 h-3" /> {prospect.company || "—"}
          </div>
          <div className="text-[0.6rem] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
            <Clock className="w-2.5 h-2.5" /> Replied {relativeTime(prospect.replied_at || prospect.updated_at)}
          </div>
        </div>
        {prospect.linkedin_url && (
          <a href={prospect.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[0.6rem] text-muted-foreground hover:text-signal-500 inline-flex items-center gap-1">
            <ExternalLink className="w-2.5 h-2.5" /> LinkedIn
          </a>
        )}
      </div>

      {isPartnership && prospect.last_reply_suggested_action && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-sm p-2.5 mb-3 text-xs" data-testid="reply-partnership-cta">
          <div className="inline-flex items-center gap-1.5 font-semibold text-emerald-500 mb-1">
            <Handshake className="w-3.5 h-3.5" /> Take over now — agency-relationship moment
          </div>
          <div className="text-foreground/80">{prospect.last_reply_suggested_action}</div>
        </div>
      )}
      {!isPartnership && prospect.last_reply_suggested_action && (
        <div className="text-[0.7rem] text-muted-foreground mb-3 inline-flex items-start gap-1.5">
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{prospect.last_reply_suggested_action}</span>
        </div>
      )}

      {prospect.last_reply_excerpt && (
        <blockquote className={`border-l-2 pl-3 py-1 text-xs italic mb-3 ${isPartnership ? "border-emerald-500/60" : "border-destructive/60"}`}>
          &quot;{prospect.last_reply_excerpt}&quot;
        </blockquote>
      )}

      {(erp.in_contacts || erp.offers_count > 0 || erp.bookings_count > 0) && (
        <div className="bg-signal-500/5 border border-signal-500/30 rounded-sm p-2 text-[0.65rem] mb-3 inline-flex flex-wrap gap-x-3 gap-y-0.5">
          <span className="inline-flex items-center gap-1 font-semibold"><History className="w-3 h-3" /> ERP history:</span>
          {erp.offers_count > 0 && <span>{erp.offers_count} prior offers</span>}
          {erp.bookings_count > 0 && <span>{erp.bookings_count} bookings</span>}
          {erp.total_revenue_eur > 0 && <span className="inline-flex items-center gap-1"><Wallet className="w-2.5 h-2.5" /> €{Math.round(erp.total_revenue_eur).toLocaleString("en-US")}</span>}
        </div>
      )}

      {isQuote && (
        <div className="bg-signal-500/5 border border-signal-500/40 rounded-sm p-3 mb-3" data-testid={`reply-quote-brain-${prospect.id}`}>
          <div className="inline-flex items-center gap-1.5 font-semibold text-signal-500 text-xs mb-2">
            <Brain className="w-3.5 h-3.5" /> Quote Brain — 14,565-offer archive
          </div>
          {brainLoading && <div className="text-[0.65rem] text-muted-foreground">Analysing similar past offers…</div>}
          {brain && brain.ok && (
            <>
              <div className="text-[0.7rem] text-foreground/80 mb-2 italic">{brain.advice}</div>
              {(brain.extracted?.origin || brain.extracted?.destination || brain.extracted?.container_type) && (
                <div className="text-[0.6rem] font-mono text-muted-foreground mb-2">
                  Extracted: {brain.extracted.origin || "?"} → {brain.extracted.destination || "?"}
                  {brain.extracted.mode && <span> · {brain.extracted.mode}</span>}
                  {brain.extracted.container_type && <span> · {brain.extracted.container_type}</span>}
                  {brain.extracted.weight_kg && <span> · {brain.extracted.weight_kg} kg</span>}
                </div>
              )}
              {brain.price_eur_percentiles?.p50 && (
                <div className="grid grid-cols-3 gap-2 text-[0.65rem] mb-2" data-testid={`reply-quote-pct-${prospect.id}`}>
                  <PctBox label="25th" value={brain.price_eur_percentiles.p25} />
                  <PctBox label="Median" value={brain.price_eur_percentiles.p50} highlight />
                  <PctBox label="75th" value={brain.price_eur_percentiles.p75} />
                </div>
              )}
              {brain.top_matches?.length > 0 && (
                <div className="space-y-1 mt-2">
                  <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground inline-flex items-center gap-1">
                    <TrendingUp className="w-2.5 h-2.5" /> Top {Math.min(brain.top_matches.length, 5)} similar offers
                  </div>
                  {brain.top_matches.slice(0, 5).map((o) => (
                    <div key={o.erp_id || o.id} className="text-[0.65rem] border-l border-border/60 pl-2 py-0.5">
                      <div className="font-mono text-foreground/70 truncate">
                        • {(o.origin || "?")} → {(o.destination || "?")}
                        {o.container_type && <span className="text-muted-foreground"> · {o.container_type}</span>}
                        {o.price_amount && <span className="text-emerald-500 ml-1">€{Math.round(o.price_amount).toLocaleString("en-US")}</span>}
                        {o.sent_at && <span className="text-muted-foreground ml-2">{relativeTime(o.sent_at)}</span>}
                        <span className="text-muted-foreground ml-2 italic">[{o._match_tier}]</span>
                      </div>
                      {o.body_prices_eur?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`reply-quote-bodyprices-${o.erp_id || o.id}`}>
                          {o.body_prices_eur.map((amt, i) => (
                            <span key={`${o.erp_id || o.id}-p${i}`} className="px-1 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500 text-[0.55rem] font-mono">
                              €{amt.toLocaleString("en-US")}
                            </span>
                          ))}
                        </div>
                      )}
                      {o.body_excerpt && (
                        <div className="text-[0.6rem] text-muted-foreground italic mt-0.5 line-clamp-2">
                          “{o.body_excerpt}”
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" onClick={onHandoff} disabled={busy} data-testid={`reply-handoff-${prospect.id}`} className="rounded-sm">
          {isPartnership ? (
            <><Handshake className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Handing off…" : "Take over for negotiation"}</>
          ) : (
            <><Flame className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Handing off…" : "Hand off to ERP"}</>
          )}
        </Button>
        {prospect.email && (
          <a
            href={`mailto:${prospect.email}`}
            className="text-[0.65rem] text-signal-500 hover:underline inline-flex items-center gap-1"
            data-testid={`reply-email-${prospect.id}`}
          >
            <Mail className="w-3 h-3" /> {prospect.email}
          </a>
        )}
        {onSkip && (
          <Button size="sm" variant="ghost" onClick={onSkip} disabled={busy}
            className="rounded-sm ml-auto text-muted-foreground hover:text-foreground"
            data-testid={`reply-skip-${prospect.id}`}>
            <XCircle className="w-3.5 h-3.5 mr-1" /> Skip
          </Button>
        )}
      </div>
    </div>
  );
}

export function ApprovalCard({ prospect, onApprove, onReject, busy }) {
  const isLinkedIn = !!prospect.linkedin_provider_id;
  const [note, setNote] = useState(prospect.drafts?.invite_note || "");
  const [subject, setSubject] = useState(prospect.drafts?.email_subject || "");
  const [bodyText, setBodyText] = useState(prospect.drafts?.email_body || "");
  const [generating, setGenerating] = useState(false);
  const [tried, setTried] = useState(false);
  const erp = prospect.erp_relationship || {};

  const emptyDraft = isLinkedIn ? !note.trim() : (!subject.trim() || !bodyText.trim());

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/sequencer/approval/${prospect.id}/draft`);
      const d = data.drafts || {};
      setNote(d.invite_note || "");
      setSubject(d.email_subject || "");
      setBodyText(d.email_body || "");
    } catch {
      toast.error("Could not compose a draft — try Regenerate.");
    }
    setGenerating(false);
  }, [prospect.id]);

  // Auto-compose a message the first time a card with no draft is shown.
  useEffect(() => {
    if (emptyDraft && !tried && !generating) {
      setTried(true);
      generate();
    }
  }, [emptyDraft, tried, generating, generate]);

  const canApprove = !busy && !generating && (isLinkedIn ? (note.trim() && note.length <= 280) : (subject.trim() && bodyText.trim()));
  const approve = () => onApprove(
    isLinkedIn
      ? { edited_invite_note: note }
      : { edited_email_subject: subject, edited_email_body: bodyText },
  );

  return (
    <div className="data-card border-amber-accent/30 bg-amber-accent/5" data-testid={`approval-${prospect.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm">{prospect.full_name}</div>
          <div className="text-xs text-muted-foreground">{prospect.title}</div>
          <div className="text-xs text-signal-500 inline-flex items-center gap-1.5 mt-0.5">
            <Building2 className="w-3 h-3" /> {prospect.company || "—"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="px-2 py-1 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border bg-amber-accent/10 border-amber-accent/40 text-amber-accent">
            Fit {prospect.score}/100
          </span>
          <span className="text-[0.6rem] text-muted-foreground font-mono inline-flex items-center gap-1">
            {isLinkedIn ? <Linkedin className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
            {isLinkedIn ? "LinkedIn invite" : "Cold email"}
          </span>
        </div>
      </div>

      {prospect.fit_reasoning && (
        <div className="text-xs text-foreground/80 italic mb-3 border-l-2 border-amber-accent/40 pl-3">
          {prospect.fit_reasoning}
        </div>
      )}

      {(erp.offers_count > 0 || erp.bookings_count > 0) && (
        <div className="bg-signal-500/5 border border-signal-500/30 rounded-sm p-2 text-[0.65rem] mb-3 inline-flex flex-wrap gap-x-3">
          <span className="inline-flex items-center gap-1 font-semibold"><History className="w-3 h-3" /> ERP:</span>
          {erp.offers_count > 0 && <span>{erp.offers_count} prior offers</span>}
          {erp.bookings_count > 0 && <span>{erp.bookings_count} bookings</span>}
        </div>
      )}

      {generating ? (
        <div className="mb-3 flex items-center gap-2 text-xs text-signal-500 bg-signal-500/5 border border-signal-500/30 rounded-sm px-3 py-3" data-testid={`approval-composing-${prospect.id}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Composing a draft with AI…
        </div>
      ) : isLinkedIn ? (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Invite note (editable)</span>
            <span className={`text-[0.6rem] font-mono ${note.length > 280 ? "text-destructive" : "text-muted-foreground"}`}>{note.length} / 280</span>
          </div>
          <textarea
            data-testid={`approval-note-${prospect.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 300))}
            rows={3}
            placeholder="No draft yet — click Regenerate to compose with AI."
            className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
          />
        </div>
      ) : (
        <div className="mb-2 space-y-2">
          <div>
            <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Email subject (editable)</span>
            <input
              data-testid={`approval-subject-${prospect.id}`}
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, 120))}
              placeholder="Subject"
              className="w-full mt-1 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
            />
          </div>
          <div>
            <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Email body (editable)</span>
            <textarea
              data-testid={`approval-body-${prospect.id}`}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={5}
              placeholder="No draft yet — click Regenerate to compose with AI."
              className="w-full mt-1 px-2 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
            />
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={approve} disabled={!canApprove} data-testid={`approval-approve-${prospect.id}`} className="rounded-sm">
          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Approving…" : "Approve & send"}
        </Button>
        <Button size="sm" variant="outline" onClick={generate} disabled={busy || generating} data-testid={`approval-regenerate-${prospect.id}`} className="rounded-sm">
          {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          Regenerate
        </Button>
        <Button size="sm" variant="ghost" onClick={onReject} disabled={busy} data-testid={`approval-reject-${prospect.id}`} className="rounded-sm text-muted-foreground">
          <XCircle className="w-3.5 h-3.5 mr-1.5" /> Skip
        </Button>
      </div>
    </div>
  );
}

function PctBox({ label, value, highlight }) {
  return (
    <div className={`rounded-sm border p-1.5 text-center ${highlight ? "border-signal-500/60 bg-signal-500/10" : "border-border bg-background/40"}`}>
      <div className="text-[0.55rem] tracking-widest uppercase text-muted-foreground">{label}</div>
      <div className={`font-mono font-semibold ${highlight ? "text-signal-500" : "text-foreground"}`}>
        {value ? `€${Math.round(value).toLocaleString("en-US")}` : "—"}
      </div>
    </div>
  );
}

